import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useShell } from '../../shell/ShellContext';
import {
  buildProgressMap,
  useResetAllTutorialProgress,
  useResetTutorialChapter,
  useSetTutorialProgress,
  useTutorialProgress,
} from './useTutorialApi';
import {
  GETTING_STARTED_CHAPTER_ID,
  getChapterById,
  TUTORIAL_CHAPTERS,
} from './tutorialChapters';

const TutorialContext = createContext(null);

export const useTutorial = () => {
  const ctx = useContext(TutorialContext);
  if (!ctx) throw new Error('useTutorial must be used within TutorialProvider');
  return ctx;
};

const applyStepNavigation = async ({
  step,
  navigate,
  setActiveZone,
  setDispatchView,
  locationPathname,
}) => {
  if (step.zone) {
    setActiveZone(step.zone);
  }
  if (step.route && locationPathname !== step.route) {
    navigate(step.route);
  }
  if (step.dispatchView) {
    setDispatchView(step.dispatchView);
  }
};

export const TutorialProvider = ({ children }) => {
  const navigate = useNavigate();
  const { setActiveZone, setDispatchView } = useShell();

  const [hubOpen, setHubOpen] = useState(false);
  const [activeChapterId, setActiveChapterId] = useState(null);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [overlayReady, setOverlayReady] = useState(false);
  const autoLaunchChecked = useRef(false);

  const { data: progressRows = [], isLoading: progressLoading } = useTutorialProgress();
  const setProgressMutation = useSetTutorialProgress();
  const resetChapterMutation = useResetTutorialChapter();
  const resetAllMutation = useResetAllTutorialProgress();

  const progressByChapter = useMemo(
    () => buildProgressMap(progressRows),
    [progressRows]
  );

  const activeChapter = activeChapterId ? getChapterById(activeChapterId) : null;
  const activeStep = activeChapter?.steps?.[activeStepIndex] ?? null;
  const isRunning = Boolean(activeChapterId && overlayReady);

  const persistProgress = useCallback((chapterId, payload) => {
    setProgressMutation.mutate({ chapterId, ...payload });
  }, [setProgressMutation]);

  const prepareStep = useCallback(async (chapter, stepIndex) => {
    const step = chapter.steps[stepIndex];
    if (!step) return;

    setOverlayReady(false);

    await applyStepNavigation({
      step,
      navigate,
      setActiveZone,
      setDispatchView,
      locationPathname: window.location.pathname.replace(/^\/admin/, '') || '/',
    });

    window.setTimeout(() => {
      setOverlayReady(true);
    }, 350);
  }, [navigate, setActiveZone, setDispatchView]);

  const startChapter = useCallback(async (chapterId, fromStep = 0) => {
    const chapter = getChapterById(chapterId);
    if (!chapter) return;

    setHubOpen(false);
    setActiveChapterId(chapterId);
    setActiveStepIndex(fromStep);
    await prepareStep(chapter, fromStep);

    persistProgress(chapterId, {
      status: 'in_progress',
      lastStepIndex: fromStep,
    });
  }, [prepareStep, persistProgress]);

  const exitTour = useCallback(() => {
    setActiveChapterId(null);
    setActiveStepIndex(0);
    setOverlayReady(false);
  }, []);

  const next = useCallback(async () => {
    if (!activeChapter) return;

    const isLastStep = activeStepIndex >= activeChapter.steps.length - 1;

    if (isLastStep) {
      persistProgress(activeChapter.id, {
        status: 'completed',
        lastStepIndex: activeStepIndex,
        completedVersion: activeChapter.version,
      });
      exitTour();
      return;
    }

    const nextIndex = activeStepIndex + 1;
    setActiveStepIndex(nextIndex);
    await prepareStep(activeChapter, nextIndex);

    persistProgress(activeChapter.id, {
      status: 'in_progress',
      lastStepIndex: nextIndex,
    });
  }, [activeChapter, activeStepIndex, prepareStep, persistProgress, exitTour]);

  const back = useCallback(async () => {
    if (!activeChapter || activeStepIndex <= 0) return;

    const prevIndex = activeStepIndex - 1;
    setActiveStepIndex(prevIndex);
    await prepareStep(activeChapter, prevIndex);

    persistProgress(activeChapter.id, {
      status: 'in_progress',
      lastStepIndex: prevIndex,
    });
  }, [activeChapter, activeStepIndex, prepareStep, persistProgress]);

  const restartChapter = useCallback(async (chapterId) => {
    await resetChapterMutation.mutateAsync(chapterId);
    await startChapter(chapterId, 0);
  }, [resetChapterMutation, startChapter]);

  const openHub = useCallback(() => {
    setHubOpen(true);
  }, []);

  const closeHub = useCallback(() => {
    setHubOpen(false);
  }, []);

  const resetAll = useCallback(async () => {
    await resetAllMutation.mutateAsync();
  }, [resetAllMutation]);

  useEffect(() => {
    if (progressLoading || autoLaunchChecked.current) return;
    autoLaunchChecked.current = true;

    const gettingStartedProgress = progressByChapter[GETTING_STARTED_CHAPTER_ID];
    if (!gettingStartedProgress) {
      startChapter(GETTING_STARTED_CHAPTER_ID, 0);
    }
  }, [progressLoading, progressByChapter, startChapter]);

  const value = useMemo(() => ({
    chapters: TUTORIAL_CHAPTERS,
    hubOpen,
    openHub,
    closeHub,
    activeChapterId,
    activeChapter,
    activeStepIndex,
    activeStep,
    isRunning,
    overlayReady,
    progressByChapter,
    progressLoading,
    startChapter,
    next,
    back,
    exitTour,
    restartChapter,
    resetAll,
    isSaving: setProgressMutation.isLoading,
  }), [
    hubOpen,
    openHub,
    closeHub,
    activeChapterId,
    activeChapter,
    activeStepIndex,
    activeStep,
    isRunning,
    overlayReady,
    progressByChapter,
    progressLoading,
    startChapter,
    next,
    back,
    exitTour,
    restartChapter,
    resetAll,
    setProgressMutation.isLoading,
  ]);

  return (
    <TutorialContext.Provider value={value}>
      {children}
    </TutorialContext.Provider>
  );
};
